import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'

import {
  ALL_RATIO_CATALOG_BUNDLES_QUERY,
  SINGLE_RATIO_CATALOG_BUNDLE_QUERY,
  RATIO_CATALOG_BUNDLES_DEPENDENTS_QUERY,
  CREATE_RATIO_CATALOG_BUNDLE_MUTATION,
  UPDATE_RATIO_CATALOG_BUNDLE_MUTATION,
  DELETE_RATIO_CATALOG_BUNDLE_MUTATION,
  FORCE_DELETE_RATIO_CATALOG_BUNDLE_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioCatalogBundleRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_RATIO_CATALOG_BUNDLE_QUERY, variables).then((data: any) => {
      return hydrateObject(data.ratioCatalogBundle)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_RATIO_CATALOG_BUNDLES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.ratioCatalogBundles)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_RATIO_CATALOG_BUNDLE_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.ratioCatalogBundle),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_RATIO_CATALOG_BUNDLES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.ratioCatalogBundles),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(RATIO_CATALOG_BUNDLES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([RATIO_CATALOG_BUNDLES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_RATIO_CATALOG_BUNDLE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.ratioCatalogBundle)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_RATIO_CATALOG_BUNDLE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.ratioCatalogBundle)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_RATIO_CATALOG_BUNDLE_MUTATION, variables).then((data: any) => {
      return data.deleteRatioCatalogBundle
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_RATIO_CATALOG_BUNDLE_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioCatalogBundle
    })
  },
}

export default ratioCatalogBundleRepository
